import { useEffect, useRef, useState } from 'react'

import { fetchFantasy, fetchGame, fetchTonight } from './api.js'
import { tabIsVisible } from '../music.js'
import { instantOf } from '../freshness.js'

// How often the hoops page asks, and when it stops. P6-328b (GN-1b).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE PAGE ASKS ABOUT AS OFTEN AS THE BOT PUBLISHES, AND NO OFTENER
// ═══════════════════════════════════════════════════════════════════════════
//
// `cogs/hoops/collector.py` fetches the NBA every `LIVE_POLL_SECONDS` (15) while
// a game is live or near a tip, and every fifteen minutes otherwise. Asking the
// Function more often than that returns the same bytes (and spends an R2 read
// for it); asking less often adds lag to a page people watch a game on. Three of
// the numbers below are therefore the COLLECTOR'S, in milliseconds, and
// `tests/test_hoops_site_fixtures.py` reads this file and fails if any of them
// stops being the bot's figure (CLAUDE.md gotcha 21: two copies of one policy,
// neither knowing the other exists).

/** While anything is live or about to be. The collector's `LIVE_POLL_SECONDS`. */
export const LIVE_POLL_MS = 15_000

/** The fast cadence begins this long before a tip. The collector's `NEAR_TIP_SECONDS`. */
export const NEAR_TIP_MS = 15 * 60_000

/** A game "not started" this long after its listed tip is postponed, not about to
 * begin, and stops holding the fast cadence. The collector's `POSTPONED_AFTER_SECONDS`
 * (it stops FETCHING the game there too, so asking faster would be asking for nothing). */
export const LATE_TIP_MS = 2 * 60 * 60_000

/** Otherwise, five minutes: the brief's figure. Nothing moves on a quiet board but a tip
 * coming into range, and `NEAR_TIP_MS` is what notices that. */
export const IDLE_POLL_MS = 5 * 60_000

/** After a read that failed when nothing has ever been read: look again soon, because the
 * page is showing nothing and a first request that hit a hiccup should not cost five
 * minutes. Not from the brief; the failure case is the page's own to decide. */
export const RETRY_MS = 30_000

/**
 * Is this card a pregame game whose tip is near, or late, enough that it may flip to live
 * at any moment?
 *
 * ⚠️ BOTH SIDES OF THE TIP. "A tip within fifteen minutes" read literally is only the side
 * before it, and the recorded evening shows why that is not enough: MIA @ TOR was listed at
 * 23:00 and was under way at 23:13, and the board said pregame for another seven minutes
 * after that. A card that is still pregame at 23:05 is the one most likely to change in the
 * next fifteen seconds, so the window runs from `NEAR_TIP_MS` before the listed tip to
 * `LATE_TIP_MS` after it — the bot's own line, no more.
 */
export function tipIsNear(card, nowMs) {
  if (card?.status !== 'pregame') return false
  const tip = instantOf(card.tip_utc)
  if (tip === null) return false
  const untilTip = tip - nowMs
  return untilTip <= NEAR_TIP_MS && -untilTip <= LATE_TIP_MS
}

/**
 * How long to wait before asking for the board again, given the last board read.
 *
 * Fast while any card is live or a tip is near; otherwise slow. `null` board (nothing has
 * ever been read) is `RETRY_MS`. Pure, so the cadence is asserted as a table and not by
 * waiting.
 */
export function tonightPollDelay(tonight, nowMs) {
  if (!tonight) return RETRY_MS
  const games = Array.isArray(tonight.games) ? tonight.games : []
  if (games.some((card) => card.status === 'live')) return LIVE_POLL_MS
  if (games.some((card) => tipIsNear(card, nowMs))) return LIVE_POLL_MS
  return IDLE_POLL_MS
}

/**
 * How long to wait before asking for one game again, or `null` for NEVER.
 *
 * 🔴 NEVER AFTER FINAL. A final game's object stops changing (the producer's digest freezes
 * it), so every request after the last one is an R2 read for bytes already held. `null` is
 * what ends the chain: nothing is scheduled, and nothing will be until the game id changes.
 */
export function gamePollDelay(game) {
  if (!game) return RETRY_MS
  if (game.status === 'final') return null
  return LIVE_POLL_MS
}

/**
 * One read, kept fresh while somebody is looking.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 A CHAIN OF TIMEOUTS, NOT AN INTERVAL, AND THE NEXT ONE IS SET WHEN THE LAST ANSWERS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `useMusic` uses a fixed `setInterval` because its cadence never changes. Here the delay
 * depends on the answer (a game going final ends the chain; a tip coming near shortens it),
 * and a request that outlives its interval must not stack a second behind it, so each answer
 * decides when the next question is asked.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 A HIDDEN TAB ASKS NOTHING, AND COMING BACK ASKS AT ONCE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `tabIsVisible` is `music.js`'s — one answer to "is anybody looking", with its direction
 * (`=== 'visible'`, not `!== 'hidden'`) inherited rather than restated: an environment this
 * file has never heard of gets silence, not a timer. `visibilitychange` clears the pending
 * timeout on the way out and, on the way back, asks IMMEDIATELY: the first thing somebody
 * does on returning to a tab is read it, and what they read should not be what was on it
 * when they left. A request already in flight is not doubled; its answer schedules the next.
 *
 * ⚠️ WHAT STAYS WHEN A READ FAILS. The last good answer. A failed read changes nothing the
 * page is showing, so a card that was live goes stale on its own clock (its `as_of` stops
 * moving) instead of vanishing — HP5, "what it last knew, and how old that is". Only a page
 * that has NEVER read the board has nothing to keep, and that is `unread`.
 */
function usePolledRead({ read, delayFor, resetKey }) {
  const [snapshot, setSnapshot] = useState({ data: null, failed: false })
  // The latest closures, so the long-lived effect below never reads stale props and the
  // effect itself restarts only when the READ'S IDENTITY (`resetKey`) changes.
  const latest = useRef({ read, delayFor })
  latest.current = { read, delayFor }

  useEffect(() => {
    let cancelled = false
    let timer = null
    let inFlight = false
    let lastGood = null

    // A new subject (a different game) starts from nothing, not from the old one's answer.
    setSnapshot({ data: null, failed: false })

    const clear = () => {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    }

    const arm = () => {
      clear()
      if (cancelled || !tabIsVisible()) return
      const delay = latest.current.delayFor(lastGood, Date.now())
      if (delay === null) return
      timer = setTimeout(run, delay)
    }

    // ⚠️ NO VISIBILITY CHECK HERE, ON PURPOSE. A timer exists only when `arm` saw a visible tab and
    // `onVisibility` clears it the moment the tab hides, so `run` is never reached while hidden; a
    // second check in this function could never fire, and a guard nothing can trip is a mutant that
    // survives by construction (`hoops-poll.test.jsx` drives each of the two that DO matter).
    async function run() {
      timer = null
      if (cancelled || inFlight) return
      inFlight = true
      const answer = await latest.current.read()
      inFlight = false
      if (cancelled) return
      if (answer.ok) {
        lastGood = answer
        setSnapshot({ data: answer, failed: false })
      } else {
        // Keep whatever is already on screen; only say `failed` so a page with nothing knows
        // it has asked and been refused, which is not the same as not having asked yet.
        setSnapshot((previous) => ({ data: previous.data, failed: true }))
      }
      arm()
    }

    const onVisibility = () => {
      if (cancelled) return
      if (!tabIsVisible()) {
        clear()
        return
      }
      // Visible again (or visible at mount): ask now, unless an answer is already on its way.
      if (!inFlight) {
        clear()
        run()
      }
    }

    const doc = globalThis.document
    doc?.addEventListener?.('visibilitychange', onVisibility)
    onVisibility()
    return () => {
      cancelled = true
      clear()
      doc?.removeEventListener?.('visibilitychange', onVisibility)
    }
  }, [resetKey])

  return snapshot
}

export const LOADING = 'loading'
export const READY = 'ready'
export const UNREAD = 'unread'

/**
 * Tonight's board, kept live while somebody is looking.
 *
 * `{ state, tonight, teams }`: `loading` until the first answer, `ready` once there is one
 * (and it stays `ready` through later failures, with the last copy), `unread` when the first
 * answer was a failure and nothing has ever been read.
 *
 * ⚠️ ONE READER PER PANEL AND NO MODULE-LEVEL CACHE, the trade `movie.js`, `music.js`,
 * `watch.js` and `directory.js` all made: a promise cache saves a request and buys shared
 * mutable state every test has to reset.
 */
export function useHoopsTonight() {
  const { data, failed } = usePolledRead({
    read: () => fetchTonight(),
    delayFor: (answer, nowMs) => tonightPollDelay(answer?.tonight ?? null, nowMs),
    resetKey: 'tonight',
  })
  if (data) return { state: READY, tonight: data.tonight, teams: data.teams }
  return { state: failed ? UNREAD : LOADING, tonight: null, teams: [] }
}

/**
 * One game's own object, kept live while it is live and never after it is final.
 *
 * `{ state, game }`, with the same three states as the board's.
 */
export function useHoopsGame(gameId) {
  const { data, failed } = usePolledRead({
    read: () => fetchGame(gameId),
    delayFor: (answer) => gamePollDelay(answer?.game ?? null),
    resetKey: gameId,
  })
  if (data) return { state: READY, game: data.game }
  return { state: failed ? UNREAD : LOADING, game: null }
}

/**
 * "Now", re-sampled every second while somebody is looking.
 *
 * ⚠️ A STAMP THAT NEVER MOVES IS THE BUG THE AGE IS FOR. `src/portal/useNow.js` ticks once a
 * minute, which is right for an as-of label and wrong for `NBA · 14s`, which has to count up
 * in front of the person reading it. A second is the unit the age is printed in.
 *
 * It is the page's one clock: the age on every card, the stale rule and the "tips in N min"
 * line all read the same `nowMs`, so they cannot disagree with each other by a tick.
 *
 * Paused while the tab is hidden (a timer nobody can see is battery), and re-sampled the
 * moment it comes back so the age is true before the next tick.
 */
export function useClock(intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    let timer = null
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer)
        timer = null
      }
    }
    const sync = () => {
      if (!tabIsVisible()) {
        stop()
        return
      }
      setNow(Date.now())
      if (timer === null) timer = setInterval(() => setNow(Date.now()), intervalMs)
    }
    sync()
    const doc = globalThis.document
    doc?.addEventListener?.('visibilitychange', sync)
    return () => {
      stop()
      doc?.removeEventListener?.('visibilitychange', sync)
    }
  }, [intervalMs])
  return now
}

// ═══════════════════════════════════════════════════════════════════════════
// THE FANTASY LAYER (P6-333a, GN-6a)
// ═══════════════════════════════════════════════════════════════════════════

/** How often a member with a league asks for it again. The bot republishes it every two hours on game days at most
 * and the roster does not move during a night, so this is slow on purpose; an ABSENT member (no league) asks no
 * oftener, and a failure asks sooner. */
export const FANTASY_POLL_MS = 30 * 60_000
export const FANTASY_RETRY_MS = 5 * 60_000

/**
 * The member's own league copy, or `null`.
 *
 * `null` is every way of having nothing to show: not in a league (a 404), not read yet, refused. The page then draws
 * NOTHING fantasy-shaped (GN2), so there is one state to render and no loading copy. A copy that has been read stays
 * through a later failed read (HP5: what it last knew), and a later ABSENT is the member being unlinked.
 */
export function useHoopsFantasy() {
  const { data } = usePolledRead({
    read: () => fetchFantasy(),
    delayFor: (answer) => (answer ? FANTASY_POLL_MS : FANTASY_RETRY_MS),
    resetKey: 'fantasy',
  })
  return data?.fantasy ?? null
}

const EMPTY_BOXES = new Map()

/**
 * The box scores of tonight's started games, `Map(gameId -> game)`, for the one reader that needs them all at once.
 *
 * 🔴 THE BOARD DOES NOT CARRY BOX SCORES, so the crew board (everybody's night) cannot be computed from "what is
 * already loaded": that is a card per game and each game's own object is read only when somebody opens it. With a
 * league, this reads each STARTED game's object, and with none (`enabled` false) it reads NOTHING: a member who is not
 * in a league costs the bucket no extra request, and the existing hoops tests that count game reads still hold.
 *
 * The cadence is the board's (`tonightPollDelay`), a final game is read once and kept (its object is frozen), and a game
 * whose read fails keeps the copy it last had. The previous map stays on screen while a new one is read, so a game
 * starting does not flash every total to zero.
 */
export function useHoopsBoxes(cards, enabled) {
  const lastGood = useRef(new Map())
  const shown = useRef(EMPTY_BOXES)
  const started = enabled ? cards.filter((card) => card.status === 'live' || card.status === 'final') : []

  const { data } = usePolledRead({
    read: async () => {
      const next = new Map()
      await Promise.all(
        started.map(async (card) => {
          const kept = lastGood.current.get(card.id)
          if (kept && kept.status === 'final' && card.status === 'final') {
            next.set(card.id, kept)
            return
          }
          const answer = await fetchGame(card.id)
          if (answer.ok) {
            lastGood.current.set(card.id, answer.game)
            next.set(card.id, answer.game)
          } else if (kept) {
            next.set(card.id, kept)
          }
        }),
      )
      return { ok: true, boxes: next }
    },
    delayFor: () => (enabled ? tonightPollDelay({ games: cards }, Date.now()) : null),
    resetKey: `${enabled}|${started.map((card) => `${card.id}:${card.status}`).join(',')}`,
  })
  if (data?.boxes) shown.current = data.boxes
  return enabled ? shown.current : EMPTY_BOXES
}
